import { useState } from 'react';

import { TextArea } from '@iwoca/orion';
import { useLingui } from '@lingui/react/macro';
import { useFormContext } from 'react-hook-form';

import styles from './PurposeScreen.module.css';
import { useLocale } from '../../../../hooks/useLocale';
import { LOAN_PURPOSE_OPTIONS_DE } from '../../../GetStarted/GetStartedDE/loanOptions';
import { TMobileSignupForm } from '../../form/form';
import { OptionCards, TOption } from '../../OptionCards/OptionCards';
import { StepLayout } from '../../StepLayout/StepLayout';

type TPurpose = (typeof LOAN_PURPOSE_OPTIONS_DE)[number]['value'];

const PURPOSE_ICONS: Record<TPurpose, string> = {
  stock_purchase: '📦',
  bridging_loan: '🌉',
  pay_bill: '🧾',
  equipment_purchase: '🛠️',
  financing_client_order: '📑',
  pay_tax_bill: '🏛️',
  refinancing_debt: '🔁',
  pay_staff: '👥',
  marketing: '📈',
  other: '✨',
};

// DIT-678. One step, two states: card picker → (if "other") a textarea for the
// detail. Kept as internal state, not a separate registry screen, so the step
// count stays stable. Reads/writes the shared mobile signup form store
// (DIT-664). `showDetail` stays local — it's view state, not form data.
export const PurposeScreen = () => {
  const { t } = useLingui();
  const { i18n } = useLocale();
  const { register, watch, setValue } = useFormContext<TMobileSignupForm>();
  const [showDetail, setShowDetail] = useState(
    () => watch('loanPurpose') === 'other',
  );

  const loanPurpose = watch('loanPurpose');
  const loanPurposeDetails = watch('loanPurposeDetails');

  const options: TOption<TPurpose>[] = LOAN_PURPOSE_OPTIONS_DE.map(
    (option) => ({
      value: option.value,
      icon: PURPOSE_ICONS[option.value],
      label: i18n._(option.label),
    }),
  );

  return (
    <StepLayout
      stageTitle={t`Credit request`}
      heading={t`What do you want to use the loan for?`}
      subHeading={showDetail ? t`Please give us a few more details` : undefined}
      hideCta={!showDetail}
      ctaDisabled={!loanPurposeDetails}
    >
      {showDetail ? (
        <TextArea
          {...register('loanPurposeDetails')}
          rows={4}
          className={styles.detail}
          label={t`Please enter a purpose`}
        />
      ) : (
        <OptionCards
          name="purpose"
          options={options}
          value={(loanPurpose as TPurpose) || undefined}
          onChange={(value) => {
            setValue('loanPurpose', value, { shouldDirty: true });
            if (value === 'other') {
              setShowDetail(true);
            }
            // TODO(0.6): non-"other" advances to the next screen via useMobileSignupNav.
          }}
        />
      )}
    </StepLayout>
  );
};
